import Vue from "vue";
import Router from "vue-router";
import { persistUtms } from "@/utils/utmStorage";

Vue.use(Router);

// A company_label so existe na URL. Quem abre o app pelo icone da tela inicial
// nao tem URL para ler, entao guardamos a ultima. Chave nova de proposito: a
// chave "label" e lida em varios lugares e nunca escrita, e passar a escreve-la
// mudaria o comportamento deles (ver Custom.vue).
const LAST_COMPANY_LABEL_KEY = "pwa.lastCompanyLabel.v1";

// Mesmo destino que o auto-login usa quando chega sem empresa. NAO usar
// "anonima" aqui: aquele nao e um portal generico, e o modo de vaga anonima, e
// ele pressupoe vacancy_id -- sem um, o cadastro sai como
// `/talents/anonimo/register/undefined` (SignUpForm.vue), a navbar se apresenta
// como "Empresa anonima" (FullPage.vue) e a aba Inicio fica desabilitada.
const FALLBACK_COMPANY_LABEL = "recrutei-empregos";

// Label do modo vaga anonima, que nunca deve ser guardado como "ultima empresa".
const ANONYMOUS_COMPANY_LABEL = "anonima";

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      // Entrada do app instalado. Nenhuma outra rota casa "/", porque todas
      // exigem :company_label -- sem isto o icone abre em branco, e o mesmo
      // vale para o clique numa notificacao cujo payload nao traz url.
      path: "/",
      name: "pwa-entry",
      redirect: (to) => ({
        name: hasAuthToken() ? "dashboard-vacancy" : "signup",
        params: { company_label: readLastCompanyLabel() },
        query: to.query,
      }),
    },
    {
      path: "/auto-login/:token",
      name: "auto-login",
      component: () => import("./views/pages/AutoLogin.vue"),
      props: true,
      meta: {
        allowAnonymous: true,
      },
    },
    {
      path: "/:company_label/job-invite/:token",
      name: "job-invite",
      component: () => import("./views/pages/InviteJobDescription.vue"),
      props: true,
      meta: {
        allowAnonymous: true,
      },
    },
    {
      path: "/:company_label/:vacancy_id(\\d+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "",
          redirect: "signup",
        },
        {
          path: "register",
          redirect: "signup",
        },
        {
          path: "signup",
          name: "signup",
          component: () => import("./views/pages/SignUp.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "login",
          redirect: "signin",
        },
        {
          path: "signin",
          name: "signin",
          component: () => import("./views/pages/SignIn.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "signin/cpf",
          name: "signin-cpf",
          component: () => import("./views/pages/SignInCpf.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "notfound",
          name: "notfound",
          component: () => import("./views/pages/Error404.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "forgot-password",
          name: "forgot-password",
          component: () => import("./views/pages/ForgotPassword.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "successfull/application",
          name: "successfull-application",
          component: () => import("./views/pages/Application.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "confirmation",
          name: "confirmation",
          component: () => import("./views/pages/Confirmation.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "successfull/application",
          name: "successfull-application",
          component: () => import("./views/pages/Application.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
        {
          path: "vacancy/detail",
          name: "vacancy-detail",
          component: () => import("./views/pages/DetailVacancy.vue"),
          props: true,
        },
        {
          path: "vacancy/:vacancyId/pre-interview",
          name: "pre-interview",
          component: () => import("./views/pages/PreInterview.vue"),
          props: true,
        },
        {
          path: "formation/save",
          name: "formation-save",
          component: () => import("./views/pages/FormationSave.vue"),
          props: true,
        },
        {
          path: "language/save",
          name: "language-save",
          component: () => import("./views/pages/LanguageSave.vue"),
          props: true,
        },
        {
          path: "skill/save",
          name: "skill-save",
          component: () => import("./views/pages/SkillSave.vue"),
          props: true,
        },
        {
          path: "edit/professional/details",
          name: "edit-professional-details",
          component: () =>
            import(
              "./views/pages/professional-detail/ProfessionalDetailEdit.vue"
            ),
        },
        {
          path: "presentation/letter",
          name: "presentation-letter",
          component: () => import("./views/pages/PresentationLetter.vue"),
        },
        {
          path: "experience/save",
          name: "experience-save",
          component: () => import("./views/pages/ExperienceSave.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/:company_label/:vacancy_id(\\d+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?",
      component: () => import("@/layouts/custom/Custom.vue"),
      children: [
        {
          path: "step/basic-info",
          name: "step-basic-info",
          component: () => import("./views/steps/Basic.vue"),
        },
        {
          path: "step/complementary-questions",
          name: "step-complementary-questions",
          component: () => import("./views/steps/ComplementaryQuestions.vue"),
        },
        {
          path: "step/diversity",
          name: "step-diversity",
          component: () => import("./views/steps/Diversity.vue"),
        },
        {
          path: "step/address",
          name: "step-address",
          component: () => import("./views/steps/Address.vue"),
        },
        {
          path: "step/formation",
          name: "step-formation",
          component: () => import("./views/steps/Formation.vue"),
        },
        {
          path: "step/experiences",
          name: "step-experiences",
          component: () => import("./views/steps/Experiences.vue"),
        },
        {
          path: "step/languages",
          name: "step-languages",
          component: () => import("./views/steps/Languages.vue"),
        },
        {
          path: "step/skills",
          name: "step-skills",
          component: () => import("./views/steps/Skills.vue"),
        },
        {
          path: "step/revision",
          name: "step-revision",
          component: () => import("./views/steps/Revision.vue"),
        },
      ],
    },
    {
      path: "/:company_label",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "redefine-password/:code",
          name: "redefine-password",
          component: () => import("./views/pages/RedefinePassword.vue"),
          meta: {
            allowAnonymous: true,
          },
        },
      ],
    },
    {
      path: "/:company_label",
      component: () => import("@/layouts/step/StepEdit.vue"),
      children: [
        {
          path: "edit/basic-info",
          name: "edit-basic-info",
          component: () => import("./views/steps/Basic.vue"),
        },
        {
          path: "edit/complementary-questions",
          name: "edit-complementary-questions",
          component: () => import("./views/steps/ComplementaryQuestions.vue"),
        },
        {
          path: "edit/diversity",
          name: "edit-diversity",
          component: () => import("./views/steps/Diversity.vue"),
        },
        {
          path: "edit/address",
          name: "edit-address",
          component: () => import("./views/steps/Address.vue"),
        },
        {
          path: "edit/formation",
          name: "edit-formation",
          component: () => import("./views/steps/Formation.vue"),
        },
        {
          path: "edit/experiences",
          name: "edit-experiences",
          component: () => import("./views/steps/Experiences.vue"),
        },
        {
          path: "edit/languages",
          name: "edit-languages",
          component: () => import("./views/steps/Languages.vue"),
        },
        {
          path: "edit/skills",
          name: "edit-skills",
          component: () => import("./views/steps/Skills.vue"),
        },
      ],
    },
    {
      path: "/:company_label/:vacancy_id(\\d+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?",
      name: "dashboard",
      component: () => import("@/layouts/dashboard/Dashboard.vue"),
      children: [
        {
          path: "apply",
          name: "quick-apply",
          component: () => import("./views/pages/QuickApply.vue"),
        },
        {
          path: "profile/detail",
          name: "dashboard-profile-detail",
          component: () => import("./views/dashboard/ProfileDetail.vue"),
        },
        {
          path: "dashboard/info",
          name: "dashboard-info",
          component: () => import("./views/dashboard/Dashboard.vue"),
        },
        {
          path: "vacancy",
          name: "dashboard-vacancy",
          component: () => import("./views/dashboard/Vacancy.vue"),
        },
        {
          path: "meeting",
          name: "dashboard-meeting",
          component: () => import("./views/dashboard/Meeting.vue"),
        },
        {
          path: "settings",
          name: "dashboard-settings",
          component: () => import("./views/dashboard/Settings.vue"),
        },
      ],
    },
    {
      path: "/:company_label/:vacancy_id(\\d+|[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})?",
      component: () => import("@/layouts/common/Common.vue"),
      children: [
        {
          path: "application-process/:step_id",
          name: "application-process",
          component: () =>
            import(
              "./views/pages/personalized-questions/PersonalizedQuestions.vue"
            ),
        },
      ],
    },
    {
      path: "/alerts/unsubscribe/:uuid",
      name: "alerts-unsubscribe",
      component: () => import("./views/pages/Alerts/Unsubscribe.vue"),
      props: false,
      meta: {
        allowAnonymous: true,
      },
      beforeEnter: (to, from, next) => {
        localStorage.setItem("userToken", null);
        next();
      },
    },
    {
      path: "/alerts/subscribe/:uuid",
      name: "alerts-subscribe",
      component: () => import("./views/pages/Alerts/Subscribe.vue"),
      props: false,
      meta: {
        allowAnonymous: true,
      },
      beforeEnter: (to, from, next) => {
        localStorage.setItem("userToken", null);
        next();
      },
    },
    {
      path: "/aceite/propostas/:uuid",
      name: "propostas",
      component: () => import("./views/pages/Proposal.vue"),
      props: false,
      meta: {
        allowAnonymous: true,
      },
      beforeEnter: (to, from, next) => {
        localStorage.setItem("userToken", null);
        next();
      },
    },
  ],
});

const AUTH_ENTRY_ROUTES = ["signup", "signin", "signin-cpf"];

router.beforeEach((to, from, next) => {
  // Antes de qualquer redirecionamento: os `next()` para dashboard-vacancy e
  // signup remontam a rota sem a query (so o de quick-apply a repassa), e quem
  // chega por push/e-mail em telas que nao capturam utm (a aba de
  // Oportunidades, por exemplo) perderia a origem sem nada quebrar.
  persistUtms(to.query);

  // Talento ja logado caindo em signup/login de uma vaga: leva ao quick-apply
  // (tela interna que confirma a vaga e candidata sem refazer login/steps).
  if (shouldRedirectLoggedUser(to)) {
    if (to.params.vacancy_id) {
      return next({
        name: "quick-apply",
        params: { ...to.params },
        query: to.query,
        replace: true,
      });
    }
    return next({
      name: "dashboard-vacancy",
      params: { ...to.params },
      replace: true,
    });
  }

  if (shouldRedirectAnonymousUser(to)) {
    next({
      name: "signup",
      params: {
        ...to.params,
      },
      replace: true,
    });
  } else next();
});

router.afterEach((to) => {
  rememberCompanyLabel(to.params && to.params.company_label);
});

export default router;

function shouldRedirectAnonymousUser(to) {
  return !to.meta.allowAnonymous && !hasAuthToken();
}

function shouldRedirectLoggedUser(to) {
  return (
    AUTH_ENTRY_ROUTES.includes(to.name) &&
    to.params.company_label !== ANONYMOUS_COMPANY_LABEL &&
    hasAuthToken()
  );
}

function hasAuthToken() {
  try {
    return !!localStorage.getItem("userToken");
  } catch (e) {
    return false;
  }
}

function readLastCompanyLabel() {
  try {
    return (
      localStorage.getItem(LAST_COMPANY_LABEL_KEY) || FALLBACK_COMPANY_LABEL
    );
  } catch (e) {
    return FALLBACK_COMPANY_LABEL;
  }
}

function rememberCompanyLabel(label) {
  // Vaga anonima nao identifica empresa: guardar "anonima" apagaria a empresa
  // real de quem passou por la depois de se candidatar.
  if (!label || label === ANONYMOUS_COMPANY_LABEL) return;
  try {
    localStorage.setItem(LAST_COMPANY_LABEL_KEY, label);
  } catch (e) {
    // Modo privado ou storage cheio: seguimos com o fallback.
  }
}
